// pages/AuxiliaryFolderDetail.js
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';

export default function AuxiliaryFolderDetail() {
  const { id, folderId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const domain = (searchParams.get('domain') || localStorage.getItem('domain') || 'toshba').toLowerCase();
  const chapter = searchParams.get('chapter') || '';

  const [folder, setFolder] = useState(null);
  const [tractateName, setTractateName] = useState(location.state?.tractateName || '');
  const [chapterTitle, setChapterTitle] = useState(location.state?.chapterTitle || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/auxiliary-folders/${folderId}/`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!ignore) setFolder(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת התיקייה');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [folderId]);

  useEffect(() => {
    if (!id || tractateName) return;
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/`);
        if (res.ok) {
          const data = await res.json();
          if (!ignore) setTractateName(data?.name || '');
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, [id, tractateName]);

  useEffect(() => {
    if (!id || !chapter || chapterTitle) return;
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${encodeURIComponent(domain)}`);
        if (!res.ok) return;
        const data = await res.json();
        const match = (data || []).find((c) => String(c.id) === String(chapter));
        const title =
          match?.display?.trim()
          || match?.title?.trim()
          || (match?.number != null ? `פרק ${match.number}` : '');
        if (!ignore) setChapterTitle(title || '');
      } catch {}
    })();
    return () => { ignore = true; };
  }, [id, chapter, chapterTitle, domain]);

  const fileNameFromUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').pop() || 'קובץ');
    } catch {
      return 'קובץ';
    }
  };

  const resourceTitle = (r) => {
    if (r?.title?.trim()) return r.title.trim();
    if (r?.file) return fileNameFromUrl(r.file);
    if (r?.url) {
      try {
        const u = new URL(r.url);
        return u.hostname.replace(/^www\./, '') || r.url;
      } catch {
        return r.url;
      }
    }
    return 'פריט';
  };

  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = tractateName ? `${tractateName} (${domText})` : 'מסכת';

  const crumbs = [{ to: `/tractates?domain=${domain}`, label: 'מסכתות' }];
  if (id) {
    crumbs.push({ to: `/tractates/${id}?domain=${domain}`, label: tractateCrumbLabel });
  }
  if (id && chapter) {
    crumbs.push({
      to: `/tractates/${id}/topics?domain=${domain}&chapter=${encodeURIComponent(chapter)}`,
      label: chapterTitle ? `סוגיות ${chapterTitle}` : 'סוגיות',
    });
  }
  crumbs.push({ label: folder?.title || 'תיקייה' });

  const backHref = id
    ? (chapter
      ? `/tractates/${id}/topics?domain=${domain}&chapter=${encodeURIComponent(chapter)}`
      : `/tractates/${id}?domain=${domain}`)
    : '/tractates';

  return (
    <div className="container">
      <Breadcrumbs items={crumbs} />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>{folder?.title || 'תיקייה'}</h2>
      </div>

      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to={backHref} state={{ tractateName, chapterTitle }}>
          חזרה
        </Link>
      </p>

      {loading && <p className="muted">טוען…</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {!loading && !error && (!folder?.resources || folder.resources.length === 0) && (
        <p className="muted">אין כרגע קבצים או קישורים בתיקייה זו.</p>
      )}

      {!loading && !error && folder?.resources?.length > 0 && (
        <div className="grid-autofit">
          {folder.resources.map((r) => {
            const title = resourceTitle(r);
            const href = r.file || r.url;
            const isLink = !!r.url && !r.file;
            return (
              <article key={r.id} className="c-card">
                {r.image ? (
                  <img className="c-card__media" src={r.image} alt={title} />
                ) : (
                  <div className="c-card__media" />
                )}
                <div className="c-card__body">
                  <h3 className="c-card__title">{title}</h3>
                  <p className="c-card__meta">{isLink ? 'קישור חיצוני' : 'קובץ'}</p>
                  {href && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <a
                        className="btn"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={r.file ? title : undefined}
                        aria-label={`${isLink ? 'פתיחת' : 'הורדת'} ${title}`}
                      >
                        {isLink ? 'פתיחה' : 'הורדה / פתיחה'}
                      </a>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

